import { PageHeader } from '../components/PageHeader';
import { ArchiveBoxIcon } from '@heroicons/react/24/outline';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type {
  ExtractionFreshness,
  ProjectFinalizeError,
  ProjectSchema,
  ProjectUndeliveredFamilies,
} from '@avisoma/shared';
import backend from '../services/backend';
import { SortableTable } from '../components/SortableTable';
import { TableSkeleton } from '../components/Skeleton';
import Pagination from '../components/Pagination';
import { Link } from '../components/Link';
import { ID } from '../components/ID';
import { Badge } from '../catalyst/badge';
import { Input } from '../catalyst/input';
import { useDeferredValue, useMemo, useState } from 'react';
import clsx from 'clsx';
import { formatDateTime } from '../lib/dateFormat';
import { ExtractionFreshnessBadge } from '../components/ExtractionFreshnessBadge';
import { UndeliveredBadge } from '../components/undeliveredFamilies';

const PAGE_SIZE = 50;

type ProjectRow = {
  id: string;
  esn?: string;
  engineType?: string;
  organisationId?: string;
  organisationName?: string;
  documentCount: number;
  pageCount?: number;
  engineEventCount?: number;
  llpPartCount?: number;
  adCount?: number;
  ldndCount?: number;
  isFinalized?: boolean;
  finalizeError?: ProjectFinalizeError;
  undeliveredFamilies?: ProjectUndeliveredFamilies;
  extractionFreshness?: ExtractionFreshness;
  createdAt: string;
};

export default function Projects() {
  useDocumentTitle('Projects');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const deferredSearch = useDeferredValue(search);

  return (
    <div>
      <PageHeader
        title="Projects"
        description="Engine projects and their extracted data"
        icon={<ArchiveBoxIcon className="size-6" />}
      >
        <div className="w-full max-w-md">
          <Input
            placeholder="Search by ID, ESN, engine type or organisation…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            aria-label="Search projects"
          />
        </div>
      </PageHeader>

      <ProjectsTable search={deferredSearch} page={page} setPage={setPage} />
    </div>
  );
}

function toProjectRow(item: ProjectSchema, organisationNames: Map<string, string>): ProjectRow {
  const data = item.data;
  return {
    id: item.id,
    esn: data?.projectData?.esn ?? data?.engineEvents?.esn ?? undefined,
    engineType: data?.projectData?.engineType ?? data?.engineEvents?.engineType ?? undefined,
    organisationId: item.organisationId,
    organisationName: item.organisationId ? organisationNames.get(item.organisationId) : undefined,
    documentCount: item.documentCount ?? 0,
    pageCount: item.pageCount,
    engineEventCount: data?.engineEvents?.engineEvents?.length,
    llpPartCount: data?.llpEvents?.parts?.length,
    adCount: data?.adEvents?.ads?.length,
    ldndCount: data?.ldndEvents?.ldndStatusEvents?.length,
    isFinalized: item.isFinalized,
    finalizeError: item.finalizeError ?? undefined,
    undeliveredFamilies: item.undeliveredFamilies ?? undefined,
    extractionFreshness: item.extractionFreshness,
    createdAt: item.createdAt,
  };
}

function EmptyDash() {
  return <span className="text-gray-400 dark:text-zinc-500">—</span>;
}

const MAX_ORGANISATION_PAGES = 10;

async function fetchAllOrganisations() {
  const items: { id: string; name: string }[] = [];
  for (let page = 1; page <= MAX_ORGANISATION_PAGES; page++) {
    const res = await backend.listOrganisations({ page, limit: 100 }).then((r) => r.data);
    items.push(...res.items);
    if (!res.hasMore) break;
  }
  return items;
}

function ProjectsTable({ search, page, setPage }: { search: string; page: number; setPage: (page: number) => void }) {
  const projectsQuery = useQuery({
    queryKey: ['Projects', page],
    queryFn: () => backend.listProjects({ page, limit: PAGE_SIZE }).then((res) => res.data),
    placeholderData: keepPreviousData,
    refetchInterval: 10_000,
  });

  const organisationsQuery = useQuery({
    queryKey: ['organisations-lookup'],
    queryFn: () => fetchAllOrganisations(),
    staleTime: 60_000,
  });

  const rows = useMemo(() => {
    const organisationNames = new Map(organisationsQuery.data?.map((org) => [org.id, org.name]) ?? []);
    return (projectsQuery.data?.items ?? []).map((item) => toProjectRow(item, organisationNames));
  }, [projectsQuery.data, organisationsQuery.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      [row.id, row.esn, row.engineType, row.organisationId, row.organisationName].some((value) =>
        value?.toLowerCase().includes(term),
      ),
    );
  }, [rows, search]);

  if (projectsQuery.isLoading) {
    return <TableSkeleton />;
  }

  if (projectsQuery.error) {
    return (
      <div className="text-red-600 dark:text-red-400 text-center text-sm pt-12">
        {(projectsQuery.error as Error).message}
      </div>
    );
  }

  if (!rows.length && page === 1) {
    return (
      <div className="text-gray-500 dark:text-zinc-400 text-center text-xl pt-12 font-extralight">
        No projects found.
      </div>
    );
  }

  const hasNextPage = projectsQuery.data?.hasMore ?? false;

  return (
    <div className="flex flex-col gap-2">
      {filteredRows.length === 0 ? (
        <div className="text-gray-500 dark:text-zinc-400 text-center text-xl pt-12 font-extralight">
          {search.trim() ? <>No projects match “{search.trim()}” on this page.</> : <>No projects on this page.</>}
        </div>
      ) : (
        <div
          className={clsx(
            'overflow-hidden rounded-lg transition-opacity',
            projectsQuery.isPlaceholderData && 'opacity-50 pointer-events-none',
          )}
        >
          <SortableTable
            limitHeight={false}
            storageId="projectsTable"
            defaultSortBy="createdAt"
            defaultSortDir="desc"
            data={filteredRows}
            columns={[
              {
                accessor: 'id',
                label: 'Project',
                render: (row) => (
                  <Link to={`/projects/${row.id}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300">
                    <ID id={row.id} />
                  </Link>
                ),
              },
              {
                accessor: 'esn',
                label: 'ESN',
                sortable: true,
                render: (row) => (row.esn ? <span className="font-medium">{row.esn}</span> : <EmptyDash />),
              },
              {
                accessor: 'engineType',
                label: 'Engine Type',
                sortable: true,
                render: (row) => (row.engineType ? <Badge>{row.engineType}</Badge> : <EmptyDash />),
              },
              {
                accessor: 'organisationName',
                label: 'Organisation',
                sortable: true,
                render: (row) =>
                  row.organisationId ? (
                    <Link
                      to={`/organisations/${encodeURIComponent(row.organisationId)}`}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      {row.organisationName ?? <ID id={row.organisationId} />}
                    </Link>
                  ) : (
                    <EmptyDash />
                  ),
              },
              {
                accessor: 'documentCount',
                label: 'Documents',
                sortable: true,
                render: (row) => (
                  <span>
                    {row.documentCount}
                    {row.pageCount ? (
                      <span className="text-gray-500 dark:text-zinc-400">
                        {' · '}
                        {row.pageCount} {row.pageCount === 1 ? 'page' : 'pages'}
                      </span>
                    ) : null}
                  </span>
                ),
              },
              {
                accessor: 'extractions',
                label: 'Extracted Data',
                render: (row) => <ExtractionSummary row={row} />,
              },
              {
                accessor: 'isFinalized',
                label: 'Status',
                render: (row) => (
                  <div className="flex flex-wrap gap-1">
                    {row.isFinalized ? (
                      <Badge color="emerald">Finalized</Badge>
                    ) : row.finalizeError ? (
                      <span title={`${row.finalizeError.message} (${formatDateTime(row.finalizeError.occurredAt)})`}>
                        <Badge color="red">Finalization failed</Badge>
                      </span>
                    ) : (
                      <Badge color="zinc">In progress</Badge>
                    )}
                    <UndeliveredBadge families={row.undeliveredFamilies} />
                    <ExtractionFreshnessBadge
                      freshness={row.extractionFreshness}
                      href={`/projects/${row.id}`}
                    />
                  </div>
                ),
              },
              {
                accessor: 'createdAt',
                label: 'Created',
                sortable: true,
                render: (row) => (
                  <span className="text-gray-500 dark:text-zinc-400">{formatDateTime(row.createdAt)}</span>
                ),
              },
            ]}
            rowKey={(row) => row.id}
          />
        </div>
      )}

      {(page > 1 || hasNextPage) && <Pagination page={page} handlePageChange={setPage} hasNextPage={hasNextPage} />}
    </div>
  );
}

function ExtractionSummary({ row }: { row: ProjectRow }) {
  const badges = [
    { label: 'Engine', count: row.engineEventCount, color: 'sky' as const },
    { label: 'LLP', count: row.llpPartCount, color: 'indigo' as const },
    { label: 'AD', count: row.adCount, color: 'amber' as const },
    { label: 'LDND', count: row.ldndCount, color: 'emerald' as const },
  ].filter((badge) => badge.count !== undefined);

  if (badges.length === 0) {
    return <EmptyDash />;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {badges.map((badge) => (
        <Badge key={badge.label} color={badge.color}>
          {badge.label} {badge.count}
        </Badge>
      ))}
    </div>
  );
}
